import React, { useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import InfoTooltip from './InfoTooltip';
import { 
  Calculator, 
  TrendingUp, 
  Calendar, 
  Coins,
  PiggyBank,
  Clock,
  ArrowRight,
  Sparkles
} from 'lucide-react';

// Payout schedule labels and multipliers
const PAYOUT_CONFIG = {
  monthly: { label: 'Monthly', paymentsPerYear: 12 },
  quarterly: { label: 'Quarterly', paymentsPerYear: 4 },
  semi_annual: { label: 'Semi-Annual', paymentsPerYear: 2 },
  annual: { label: 'Annual', paymentsPerYear: 1 },
  at_exit: { label: 'At Exit', paymentsPerYear: 0 },
  none: { label: 'None', paymentsPerYear: 0 }
};

const InvestmentCalculator = ({ project }) => {
  const minInvestment = project.min_investment || 100;
  const maxInvestment = project.max_investment || 100000;
  const targetYield = project.target_yield || 0;
  const durationMonths = project.duration_months || 12;
  const payoutSchedule = project.payout_schedule || 'at_exit';
  const lockInMonths = project.capital_lock_in_period_months || 0;
  const tokenPrice = project.token_price || 1;

  const [investmentAmount, setInvestmentAmount] = useState(minInvestment);

  // Calculate projected returns
  const calculations = useMemo(() => {
    const amount = parseFloat(investmentAmount) || 0;
    const durationYears = durationMonths / 12;
    const annualReturn = amount * (targetYield / 100);
    const totalReturn = annualReturn * durationYears;
    const totalValue = amount + totalReturn;
    const tokensReceived = Math.floor(amount / tokenPrice);
    
    // Calculate per-period payouts
    const payoutConfig = PAYOUT_CONFIG[payoutSchedule] || PAYOUT_CONFIG.at_exit;
    const paymentsPerYear = payoutConfig.paymentsPerYear;
    const totalPayments = paymentsPerYear > 0 ? Math.floor(paymentsPerYear * durationYears) : 1;
    const paymentAmount = paymentsPerYear > 0 ? annualReturn / paymentsPerYear : totalReturn;

    return {
      amount,
      annualReturn,
      totalReturn,
      totalValue,
      tokensReceived,
      totalPayments,
      paymentAmount,
      roi: amount > 0 ? ((totalReturn / amount) * 100) : 0,
      durationYears
    };
  }, [investmentAmount, targetYield, durationMonths, payoutSchedule, tokenPrice]);

  const handleSliderChange = (value) => {
    setInvestmentAmount(value[0]);
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const numValue = parseInt(value) || 0;
    setInvestmentAmount(Math.min(Math.max(numValue, 0), maxInvestment));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Don't render if no yield data
  if (!targetYield || targetYield <= 0) {
    return null;
  }

  return (
    <Card className="glass p-5 border-primary/20" data-testid="investment-calculator">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-primary/20 rounded-lg">
          <Calculator className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="text-base font-heading font-semibold">Investment Calculator</h3>
          <p className="text-xs text-foreground-muted">Estimate your potential returns</p>
        </div>
      </div>

      {/* Investment Amount Input */}
      <div className="mb-5">
        <Label className="text-sm text-foreground-muted mb-2 block">Investment Amount</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted">€</span>
          <Input
            type="text"
            value={investmentAmount.toLocaleString('de-DE')}
            onChange={handleInputChange}
            className="pl-7 text-lg font-semibold bg-white/5 border-white/10 focus:border-primary"
            data-testid="investment-amount-input"
          />
        </div>
        
        {/* Slider */}
        <div className="mt-3 px-1">
          <Slider
            value={[investmentAmount]}
            min={minInvestment}
            max={Math.min(maxInvestment, 100000)}
            step={100}
            onValueChange={handleSliderChange}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-foreground-muted mt-1">
            <span>Min: {formatCurrency(minInvestment)}</span>
            <span>Max: {formatCurrency(Math.min(maxInvestment, 100000))}</span>
          </div>
        </div>
      </div>

      {/* Results Grid */}
      <div className="space-y-3">
        {/* Tokens Received */}
        <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-accent" />
            <span className="text-sm">Tokens Received</span>
          </div>
          <span className="font-semibold text-accent">{calculations.tokensReceived.toLocaleString()}</span>
        </div>

        {/* Annual Return */}
        <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            <span className="text-sm">Annual Return</span>
            <InfoTooltip content={`Based on ${targetYield}% target APY`} iconSize={12} />
          </div>
          <span className="font-semibold text-emerald-400">{formatCurrency(calculations.annualReturn)}</span>
        </div>

        {/* Payout Info */}
        {payoutSchedule && payoutSchedule !== 'none' && payoutSchedule !== 'at_exit' && (
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-info" />
              <span className="text-sm">{PAYOUT_CONFIG[payoutSchedule]?.label} Payout</span>
            </div>
            <span className="font-semibold text-info">~{formatCurrency(calculations.paymentAmount)}</span>
          </div>
        )}

        {/* Lock-in Period */}
        {lockInMonths > 0 && (
          <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" />
              <span className="text-sm">Lock-in Period</span>
            </div>
            <span className="font-semibold text-amber-400">{lockInMonths} months</span>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-white/10 my-2"></div>

        {/* Total Projected Return */}
        <div className="p-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg border border-primary/30">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Total Return ({calculations.durationYears.toFixed(1)} years)</span>
            </div>
            <span className="text-lg font-bold text-primary">{formatCurrency(calculations.totalReturn)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PiggyBank className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium">Total Value at Maturity</span>
            </div>
            <span className="text-lg font-bold text-accent">{formatCurrency(calculations.totalValue)}</span>
          </div>

          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2">
            <span className="text-xs text-foreground-muted">Your ROI:</span>
            <span className="text-sm font-bold text-emerald-400">+{calculations.roi.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-foreground-muted/60 mt-4 leading-relaxed">
        * Projections are estimates based on target returns and are not guaranteed. 
        Actual returns may vary. Past performance does not guarantee future results.
        Investment involves risk including potential loss of principal.
      </p>
    </Card>
  );
};

export default InvestmentCalculator;
